import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthGuard, EmailGuard } from "../App";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FaFileCsv, FaFilePdf, FaSort, FaTrashAlt, FaAngleDoubleLeft, FaAngleLeft, FaAngleRight, FaAngleDoubleRight } from "react-icons/fa";
import { Oval } from "react-loader-spinner";
import { StandardURL } from "../App";

const AuditLog = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const [day, setDay] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    setLoading(true);
    setError(null);
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(
        `${StandardURL}/iNyumba/view/auditlogs`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.response && Array.isArray(response.data.response)) {
        setLogs(response.data.response);
      } else {
        setLogs([]);
      }
    } catch (err) {
      setError("Failed to fetch logs.");
      toast.error(err.response?.data?.response || "Failed to fetch data.");
      console.error("Error fetching logs:", err);
    } finally {
      setLoading(false);
    }
  };

  const deleteLog = async (logId) => {
    if (!window.confirm("Are you sure you want to delete this log?")) {
      return;
    }
    const token = localStorage.getItem("token");
    try {
      await axios.delete(
        `${StandardURL}/iNyumba/delete/auditlogs/${logId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setLogs(logs.filter((log) => log.index !== logId));
      toast.success("Log deleted successfully");
    } catch (err) {
      toast.error(err.response?.data?.response || "Failed to delete log");
      console.error("Error deleting log:", err);
    }
  };

  const handleDownload = async (fileType) => {
    setLoading(true);
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(
        `${StandardURL}/iNyumba/logsreportsalltime?format=${fileType}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob",
        }
      );
      const fileExtension = fileType === "pdf" ? "pdf" : "csv";
      const fileName = `inyumbaauditlogs_alltime.${fileExtension}`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Download is successful");
    } catch (err) {
      console.error(`Error downloading ${fileType.toUpperCase()}:`, err);
      toast.error(err.response?.data?.response || "Error downloading all records.");
    } finally {
      setLoading(false);
    }
  };

  const handleFilteredDownload = async (fileType) => {
    if (!year) {
      toast.error("Year must be entered for filtered download.");
      return;
    }
    setLoading(true);
    const token = localStorage.getItem("token");
    try {
      const response = await axios.post(
        `${StandardURL}/iNyumba/logsreports?format=${fileType}`,
        { year, month, day },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob",
        }
      );
      const fileExtension = fileType === "pdf" ? "pdf" : "csv";
      const fileName = `inyumbaauditlogs_${year}${month ? `-${month}` : ''}${day ? `-${day}` : ''}.${fileExtension}`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Download is successful");
    } catch (err) {
      console.error(`Error downloading ${fileType.toUpperCase()}:`, err);
      toast.error(err.response?.data?.response || "Error downloading filtered records. Check your input or if records exist for the period.");
    } finally {
      setLoading(false);
    }
  };

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  const sortedLogs = [...logs].sort((a, b) => {
    if (sortConfig.key !== null) {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedLogs.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedLogs.length / itemsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  
  // Pagination UI rendering logic
  const pageNumbers = [];
  const maxPageNumbersToShow = 5;
  let startPage = Math.max(1, currentPage - Math.floor(maxPageNumbersToShow / 2));
  let endPage = Math.min(totalPages, startPage + maxPageNumbersToShow - 1);
  if (endPage - startPage + 1 < maxPageNumbersToShow) {
    startPage = Math.max(1, endPage - maxPageNumbersToShow + 1);
  }
  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  return (
    <EmailGuard>
      <AuthGuard>
        <div className="container mx-auto px-4 py-8 bg-gray-100 min-h-screen">
          <h1 className="text-3xl font-bold text-gray-800 text-center mb-6">Audit Logs</h1>

          <div className="bg-white shadow-md rounded-lg p-6 mb-6">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              {/* Filtered Download Section */}
              <div className="flex flex-col items-start space-y-2 w-full md:w-auto">
                <div className="text-lg font-semibold text-gray-700">Download Filtered Logs</div>
                <div className="flex items-center space-x-2 w-full">
                  <input
                    type="number"
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                    placeholder="Year (e.g., 2023)"
                    className="border border-gray-300 rounded py-2 px-3 text-sm focus:ring-blue-500 focus:border-blue-500 w-1/3 md:w-auto"
                  />
                  <input
                    type="number"
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                    placeholder="Month (1-12)"
                    className="border border-gray-300 rounded py-2 px-3 text-sm focus:ring-blue-500 focus:border-blue-500 w-1/3 md:w-auto"
                  />
                  <input
                    type="number"
                    value={day}
                    onChange={(e) => setDay(e.target.value)}
                    placeholder="Day (1-31)"
                    className="border border-gray-300 rounded py-2 px-3 text-sm focus:ring-blue-500 focus:border-blue-500 w-1/3 md:w-auto"
                  />
                </div>
                <div className="flex space-x-2 w-full">
                  <button
                    onClick={() => handleFilteredDownload("csv")}
                    className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md text-sm transition duration-150 ease-in-out flex-1"
                  >
                    <FaFileCsv className="mr-1 inline" /> CSV
                  </button>
                  <button
                    onClick={() => handleFilteredDownload("pdf")}
                    className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-md text-sm transition duration-150 ease-in-out flex-1"
                  >
                    <FaFilePdf className="mr-1 inline" /> PDF
                  </button>
                </div>
              </div>

              {/* All Records Download Section */}
              <div className="flex flex-col items-start space-y-2 w-full md:w-auto md:ml-8">
                <div className="text-lg font-semibold text-gray-700">Download All Logs</div>
                <div className="flex space-x-2 w-full">
                  <button
                    onClick={() => handleDownload("csv")}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md text-sm transition duration-150 ease-in-out flex-1"
                  >
                    <FaFileCsv className="mr-1 inline" /> CSV
                  </button>
                  <button
                    onClick={() => handleDownload("pdf")}
                    className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded-md text-sm transition duration-150 ease-in-out flex-1"
                  >
                    <FaFilePdf className="mr-1 inline" /> PDF
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            {loading ? (
              <div className="flex justify-center items-center p-8">
                <Oval color="#4F46E5" height={50} width={50} />
              </div>
            ) : error ? (
              <div className="text-center p-8 text-red-500 font-semibold">
                {error}
              </div>
            ) : sortedLogs.length === 0 ? (
              <div className="text-center p-8 text-gray-500">
                No audit logs available.
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        {["index", "user", "ipAddress", "device", "email", "description", "actionTime"].map((header) => (
                          <th
                            key={header}
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                            onClick={() => requestSort(header)}
                          >
                            <div className="flex items-center">
                              {header.replace(/_/g, ' ').toUpperCase()}
                              <FaSort className="ml-1 text-gray-400" />
                              {getSortIcon(header)}
                            </div>
                          </th>
                        ))}
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Change Made
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {currentItems.map((log) => (
                        <tr key={log.index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{log.index}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{log.user}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{log.ipAddress}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{log.device}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{log.email}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{log.description}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(log.actionTime).toLocaleString()}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500 max-w-xs overflow-hidden text-ellipsis">
                            <pre className="text-xs text-gray-600 bg-gray-100 p-2 rounded overflow-x-auto whitespace-pre-wrap">
                              {JSON.stringify(log.changeMade, null, 2)}
                            </pre>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => deleteLog(log.index)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete Log"
                            >
                              <FaTrashAlt className="h-5 w-5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {totalPages > 1 && (
                  <nav className="flex items-center justify-center space-x-2 mt-6 p-4">
                    <button
                      onClick={() => paginate(1)}
                      disabled={currentPage === 1}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FaAngleDoubleLeft />
                    </button>
                    <button
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FaAngleLeft />
                    </button>
                    {pageNumbers.map(number => (
                      <button
                        key={number}
                        onClick={() => paginate(number)}
                        className={`px-4 py-2 text-sm font-medium rounded-md border border-gray-300 transition-colors duration-150 ease-in-out
                          ${currentPage === number 
                            ? 'bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700' 
                            : 'text-gray-700 bg-white hover:bg-gray-100'}`}
                      >
                        {number}
                      </button>
                    ))}
                    <button
                      onClick={() => paginate(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FaAngleRight />
                    </button>
                    <button
                      onClick={() => paginate(totalPages)}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FaAngleDoubleRight />
                    </button>
                  </nav>
                )}
              </>
            )}
          </div>
        </div>
      </AuthGuard>
    </EmailGuard>
  );
};

export default AuditLog;